import { z } from 'zod'
import { instantSchema } from '../validators/temporal.validator.js'
import { FUNNEL_TOUR_ROUTES, type FunnelTarget } from '../enums/funnel-stage.enum.js'
import { honeybookEventProblemSchema } from '../enums/honeybook-event-problem.enum.js'
import { honeybookReviewReasonSchema } from '../enums/honeybook-review-reason.enum.js'
import { calendarDaySchema } from '../validators/calendar-day.validator.js'

/**
 * A venue's HoneyBook connection (docs/honeybook.spec.md, "Connecting"): which
 * HoneyBook company it reaches, what HoneyBook was allowed to share, and whether
 * it is working.
 */

/** A stage of the HoneyBook company's pipeline, as its last read found it. */
export const honeybookStageSchema = z.object({
  id: z.string(),
  name: z.string(),
  /** A built-in stage's fixed key (`_INQUIRY_`…); null for the company's own. */
  systemKey: z.string().nullable(),
  category: z.enum(['lead', 'booked', 'other']).nullable(),
  order: z.number(),
})

export type HoneybookStage = z.infer<typeof honeybookStageSchema>

/** What the last read found to choose from: the pipeline's stages and the meeting titles seen. */
export const honeybookCatalogSchema = z.object({
  stages: z.array(honeybookStageSchema).default([]),
  /** Meeting titles seen, by key (trimmed, lowercased, spaces collapsed) → as last written. */
  meetingTitles: z.record(z.string(), z.string()).default({}),
})

export type HoneybookCatalog = z.infer<typeof honeybookCatalogSchema>

/**
 * What a HoneyBook stage can be counted as (docs/honeybook.spec.md). Inquiry is
 * every lead; Tour scheduled and Open house RSVP come from meetings; Event day
 * and Canceled only from events.
 */
export const HONEYBOOK_STAGE_TARGETS = [
  'qualified',
  'corporate_reply',
  'venue_reply',
  'toured',
  'proposal_sent',
  'date_on_hold',
  'contract_signed',
  'deposit_paid',
  'booked',
  'lost',
] as const satisfies readonly FunnelTarget[]

/** What a meeting's title can be counted as: one of the two routes to a tour. */
export const HONEYBOOK_MEETING_TARGETS = FUNNEL_TOUR_ROUTES satisfies readonly FunnelTarget[]

/**
 * What the venue says its HoneyBook stages and meetings mean: a stage by its id,
 * a meeting by its title's key. Anything left out counts as nothing.
 */
export const honeybookFunnelMapSchema = z.object({
  stages: z.record(z.string().min(1).max(64), z.enum(HONEYBOOK_STAGE_TARGETS)).default({}),
  meetings: z.record(z.string().min(1).max(200), z.enum(HONEYBOOK_MEETING_TARGETS)).default({}),
})

export type HoneybookFunnelMap = z.infer<typeof honeybookFunnelMapSchema>

/** The permissions FLOH Venues asks HoneyBook for: it only ever reads. */
export const HONEYBOOK_PERMISSIONS = ['projects', 'workspaces'] as const

/** What doesn't work without each permission, in the venue's terms. */
export const HONEYBOOK_PERMISSION_EFFECT = {
  projects: 'Booked projects can’t become events, and leads count from when they were added rather than when the couple inquired.',
  workspaces: 'Leads can’t be counted.',
} as const satisfies Record<(typeof HONEYBOOK_PERMISSIONS)[number], string>

const HONEYBOOK_PERMISSION_EFFECTS = [
  HONEYBOOK_PERMISSION_EFFECT.projects,
  HONEYBOOK_PERMISSION_EFFECT.workspaces,
] as const

export const honeybookPermissionEffectSchema = z.enum(HONEYBOOK_PERMISSION_EFFECTS)

/**
 * Why a venue cannot start switching off HoneyBook yet. Null on the wire when
 * it can, or once a switch is pending or done.
 */
export const HONEYBOOK_SWITCH_BLOCK = {
  companyOnly: 'Only someone who sets up integrations across the company can switch a venue.',
  salesFirst: 'Switching needs this venue’s proposals, contracts and payments set up here first.',
  notWorking: 'Switching needs HoneyBook working, so it can be read one last time.',
} as const

const HONEYBOOK_SWITCH_BLOCKS = [
  HONEYBOOK_SWITCH_BLOCK.companyOnly,
  HONEYBOOK_SWITCH_BLOCK.salesFirst,
  HONEYBOOK_SWITCH_BLOCK.notWorking,
] as const

export const honeybookSwitchBlockSchema = z.enum(HONEYBOOK_SWITCH_BLOCKS)

export type HoneybookSwitchBlock = z.infer<typeof honeybookSwitchBlockSchema>

export const honeybookConnectionDtoSchema = z.object({
  venueId: z.string().uuid(),
  /** The HoneyBook company the connection reaches, named as HoneyBook names it. */
  companyName: z.string(),
  connectedAt: instantSchema,
  /** Who connected it; null once they're gone. */
  connectedByName: z.string().nullable(),
  /**
   * `first_read` until its first complete read; then `working`; `not_working`
   * while HoneyBook refuses the connection (connecting again fixes it);
   * `disconnected` after someone disconnected it; `switched` once the venue
   * switched to FLOH Venues, for good.
   */
  state: z.enum(['first_read', 'working', 'not_working', 'disconnected', 'switched']),
  /** Permissions HoneyBook wasn't given, each with what doesn't work without it. */
  missing: z.array(z.object({ permission: z.enum(HONEYBOOK_PERMISSIONS), effect: honeybookPermissionEffectSchema })),
  /** The last complete read; null before the first. */
  readAt: instantSchema.nullable(),
  /** A read is under way now. */
  reading: z.boolean(),
  /** The stages and meeting titles the last read found, to say what each means. */
  catalog: honeybookCatalogSchema,
  funnelMap: honeybookFunnelMapSchema,
  /** The track events made from booked projects start on; null = the venue as a whole. */
  eventTrackId: z.string().uuid().nullable(),
  /**
   * Booked projects with no date yet, so no event: each becomes one when
   * HoneyBook has a date. Told apart by when the couple inquired; names aren't kept.
   */
  waiting: z.array(z.object({ projectId: z.string(), inquiredAt: instantSchema, openUrl: z.string().url().nullable().default(null) })),
  /** HoneyBook events moved onto a lane another event holds. */
  clashes: z.array(
    z.object({ eventId: z.string().uuid(), eventName: z.string(), withEventId: z.string().uuid(), withEventName: z.string() }),
  ),
  /** HoneyBook events that can't follow their project, and why. */
  problems: z.array(z.object({ eventId: z.string().uuid(), eventName: z.string(), problem: honeybookEventProblemSchema })),
  /** The venue switches to FLOH Venues on this day (phase 7); null when no switch is pending. */
  switchOn: calendarDaySchema.nullable().default(null),
  /** When the venue switched; null until it has. */
  switchedAt: instantSchema.nullable().default(null),
  /** This person may start the switch now. */
  switchOffered: z.boolean().default(false),
  /** Why the switch can't be started yet; null when it can, or once it is pending or done. */
  switchBlockedBy: honeybookSwitchBlockSchema.nullable().default(null),
})

export type HoneybookConnectionDto = z.infer<typeof honeybookConnectionDtoSchema>

export const venueHoneybookDtoSchema = z.object({
  /** HoneyBook can be connected in this deployment. */
  available: z.boolean(),
  connection: honeybookConnectionDtoSchema.nullable(),
})

export type VenueHoneybookDto = z.infer<typeof venueHoneybookDtoSchema>

/**
 * Whether a venue's pipeline has HoneyBook in it, for whoever sees the pipeline
 * (the dashboard's panel): `none` with no connection, `first_read` until its
 * first read completes, then `ready`.
 */
export const venueHoneybookStatusDtoSchema = z.object({
  state: z.enum(['none', 'first_read', 'ready']),
  /** The last complete read. */
  readAt: instantSchema.nullable(),
})

export type VenueHoneybookStatusDto = z.infer<typeof venueHoneybookStatusDtoSchema>

/** A booking an event might be, as HoneyBook has it now. */
export const honeybookCandidateDtoSchema = z.object({
  projectId: z.string(),
  name: z.string().nullable(),
  date: calendarDaySchema.nullable(),
  guestCount: z.number().int().nullable(),
  /** The booking in HoneyBook's own app; null when its workspace isn't known here. */
  openUrl: z.string().url().nullable().default(null),
})

export type HoneybookCandidateDto = z.infer<typeof honeybookCandidateDtoSchema>

/** An event matching couldn't hand over on its own (docs/honeybook.plan.md, 5.4). */
export const honeybookReviewItemDtoSchema = z.object({
  id: z.string().uuid(),
  eventId: z.string().uuid(),
  eventName: z.string(),
  eventDate: calendarDaySchema.nullable(),
  eventGuestCount: z.number().int(),
  reason: honeybookReviewReasonSchema,
  /** Read from HoneyBook now; a booking it no longer shows is left out, and all are when it can't be reached. */
  candidates: z.array(honeybookCandidateDtoSchema),
  /** Index into `candidates` of Clef's pick; null when there is none or it can't be shown. */
  suggestedIndex: z.number().int().nullable(),
  suggestedProbability: z.number().nullable(),
})

export type HoneybookReviewItemDto = z.infer<typeof honeybookReviewItemDtoSchema>

export const honeybookReviewListDtoSchema = z.object({
  /** The oldest waiting, a page at a time. */
  items: z.array(honeybookReviewItemDtoSchema),
  /** How many are waiting in all. */
  total: z.number().int().nonnegative(),
})

export type HoneybookReviewListDto = z.infer<typeof honeybookReviewListDtoSchema>

/** Where to send someone to sign in to HoneyBook. */
export const honeybookSignInStartDtoSchema = z.object({ url: z.string().url() })

export type HoneybookSignInStartDto = z.infer<typeof honeybookSignInStartDtoSchema>
